import type { TFunction } from 'i18next'
import { FilePenLine, Trash2 } from 'lucide-react'

import { ClickableCellWithTooltip } from '@/components/ClickableCellWithTooltip'
import { TableColumn } from '@/components/Table'
import { FreezePosition } from '@/components/Table/tableHelper'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn, formatFullName } from '@/utils/common'

import { ERoleType, getRolesDisplayLabel } from './constants'
import { UserManagementAction, UserManagementUser, UserStatus } from './types'

const getStatusLabel = (status?: UserStatus): string => {
  if (!status) return '-'

  switch (status.toLowerCase()) {
    case 'active':
      return 'Active'
    case 'pending':
      return 'Pending'
    default:
      return status
  }
}

const getStatusBadgeClasses = (status?: UserStatus): string => {
  if (!status) return ''

  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-[--Colors-Success-50] text-[--Colors-Success-700] border-[--Colors-Success-200]'
    case 'pending':
      return 'bg-[--Colors-Warning-50] text-[--Colors-Warning-700] border-[--Colors-Warning-200]'
    default:
      return ''
  }
}

export const getUserManagementColumns = (
  handleActionClick: (
    action: UserManagementAction,
    item: UserManagementUser
  ) => void,
  t: TFunction,
  currentUserRole?: ERoleType | null
): TableColumn<UserManagementUser>[] => [
  {
    id: 'full_name',
    label: t('userManagement.columns.name'),
    width: 240,
    sortable: true,
    renderCell: (_, row) => {
      const fullName = formatFullName(row.first_name, row.last_name)
      return (
        <ClickableCellWithTooltip
          value={fullName || '-'}
          onClick={() => handleActionClick('view-detail', row)}
        />
      )
    },
  },
  {
    id: 'email',
    label: t('userManagement.columns.email'),
    width: 280,
    sortable: true,
  },
  {
    id: 'role_name',
    label: t('userManagement.columns.role'),
    width: 180,
    // currently roles are not sortable due to backend limitations
    sortable: false,
    disableTruncation: true,
    renderCell: (_, row) => getRolesDisplayLabel(row?.roles),
  },
  {
    id: 'status',
    label: t('userManagement.columns.status'),
    width: 140,
    sortable: true,
    renderCell: (_, row) => (
      <span
        className={cn(
          'inline-flex items-center justify-center text-xs px-2 py-0.5 rounded-full font-medium border border-solid',
          getStatusBadgeClasses(row.status)
        )}
      >
        {getStatusLabel(row.status)}
      </span>
    ),
  },
  {
    id: 'actions',
    label: '',
    width: 90,
    isFrozen: true,
    freezePosition: FreezePosition.RIGHT,
    renderCell: (_, row) => (
      <div className="flex gap-0.5 items-center">
        {currentUserRole === ERoleType.ORGANIZATION_ADMIN && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="text"
                className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  handleActionClick('edit', row)
                }}
              >
                <FilePenLine size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('userManagement.actions.edit')}</span>
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleActionClick('delete', row)
              }}
            >
              <Trash2 size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span>{t('userManagement.actions.delete')}</span>
          </TooltipContent>
        </Tooltip>
      </div>
    ),
  },
]
